import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";
import { ArrowLeft, Clock, BookOpen, CheckCircle, ArrowRight, AlertCircle } from "lucide-react";

const JobDescriptionAnalysisGuidePage = () => {
  return (
    <>
      <Helmet>
        <title>How to Analyze a Job Description Like a Recruiter | Hireun</title>
        <meta name="description" content="Learn to decode job descriptions strategically. Understand hidden requirements, identify must-haves vs nice-to-haves, and position yourself as the ideal candidate." />
        <meta name="keywords" content="job description analysis, decode job posting, job requirements, recruiter tips, job application strategy" />
        <link rel="canonical" href="https://hireun.com/guides/job-description-analysis" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/guides" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Guides
          </Link>

          <div className="mb-8">
            <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              Strategy
            </span>
            <div className="flex items-center text-sm text-gray-500 mt-4">
              <Clock className="w-4 h-4 mr-1" />
              <span>10 min read</span>
              <span className="mx-2">•</span>
              <span>Updated January 2025</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How to Analyze a Job Description Like a Recruiter
          </h1>

          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            Most candidates read job descriptions passively, treating them as simple lists of requirements. 
            Recruiters, however, read them strategically — as blueprints revealing exactly what success 
            looks like in the role. This guide teaches you to think like a recruiter and respond to job 
            postings with precision.
          </p>

          {/* Table of Contents */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
              In This Guide
            </h2>
            <ul className="space-y-2">
              <li><a href="#must-haves" className="text-blue-600 hover:underline">1. Separating Must-Haves from Nice-to-Haves</a></li>
              <li><a href="#problem" className="text-blue-600 hover:underline">2. Identifying the Real Problem</a></li>
              <li><a href="#language" className="text-blue-600 hover:underline">3. Decoding Ambiguous Language</a></li>
              <li><a href="#criteria" className="text-blue-600 hover:underline">4. Spotting Evaluation Criteria</a></li>
              <li><a href="#application" className="text-blue-600 hover:underline">5. Using Analysis in Your Application</a></li>
              <li><a href="#interview" className="text-blue-600 hover:underline">6. Leveraging Analysis in Interviews</a></li>
            </ul>
          </div>

          <div className="prose prose-lg max-w-none">
            <section id="must-haves" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Separating Must-Haves from Nice-to-Haves
              </h2>
              <p className="text-gray-700 mb-4">
                Every job description contains two types of requirements: those that are genuinely non-negotiable, 
                and those that are aspirational. Understanding this distinction is crucial — it determines whether 
                you should apply and how you should position yourself.
              </p>
              <p className="text-gray-700 mb-4">
                True must-haves are typically listed first and often include specific certifications, years of 
                experience in a particular domain, or technical skills that are impossible to quickly acquire. 
                Nice-to-haves appear later, often introduced with softer language like "preferred," "ideally," 
                or "bonus if you have."
              </p>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-amber-800 font-medium mb-2">Important Insight</p>
                    <p className="text-amber-700">
                      Studies show that candidates who meet 60-70% of stated requirements are often hired. 
                      Don't self-select out of roles where you meet the core requirements but lack some 
                      nice-to-haves.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How to Identify True Requirements:</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Requirements listed in the first paragraph are typically essential</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Specific years of experience ("5+ years in B2B sales") indicate firm thresholds</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Legal or compliance requirements are never negotiable</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Requirements repeated multiple times are priorities</span>
                </li>
              </ul>
            </section>

            <section id="problem" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Identifying the Real Problem the Role Solves
              </h2>
              <p className="text-gray-700 mb-4">
                Every job exists to solve a specific problem. Understanding this problem is perhaps the most 
                important insight you can gain from a job description. When you know why the role exists, you 
                can position yourself as the solution.
              </p>
              <p className="text-gray-700 mb-4">
                Ask yourself: Why does this role exist? Is the company growing and needs more capacity? Are 
                they replacing someone who left? Are they creating a new function? Each scenario implies 
                different expectations and success criteria.
              </p>

              <div className="bg-gray-100 rounded-lg p-6 my-6">
                <h4 className="font-semibold text-gray-900 mb-3">Questions to Uncover the Real Problem:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• What business outcomes does this role drive?</li>
                  <li>• What was happening before this role existed?</li>
                  <li>• What would failure in this role cost the company?</li>
                  <li>• Who benefits most from this role being filled well?</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-4">
                Often, the job description's opening paragraph reveals the core problem. Phrases like "we're 
                looking for someone to scale our..." or "seeking a leader to transform our..." indicate the 
                central challenge the role addresses.
              </p>
            </section>

            <section id="language" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Decoding Ambiguous Language
              </h2>
              <p className="text-gray-700 mb-4">
                Job descriptions often use vague language that candidates gloss over. But these ambiguous 
                phrases often reveal important information about the work environment and expectations.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden my-6">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Phrase</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">What It Often Means</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">"Fast-paced environment"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">High workload, frequent context switching, tight deadlines</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">"Dynamic team"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Processes may be undefined, expect ambiguity</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">"Wear many hats"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Limited resources, broad responsibilities, startup mentality</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">"Drive transformation"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Change management required, expect resistance</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">"Partner with stakeholders"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Influence without authority, cross-functional complexity</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">"Build from scratch"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">No existing infrastructure, high autonomy but limited support</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-4">
                Understanding these coded phrases helps you prepare more relevant examples. If the role 
                emphasizes "driving transformation," prepare stories about implementing change. If it 
                mentions "partnering with stakeholders," ready examples of cross-functional collaboration.
              </p>
            </section>

            <section id="criteria" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Spotting Hidden Evaluation Criteria
              </h2>
              <p className="text-gray-700 mb-4">
                Every job description implies how success will be measured, even if it's not explicitly stated. 
                Learning to identify these hidden criteria gives you a competitive advantage — you can 
                proactively address them in your application and interviews.
              </p>
              <p className="text-gray-700 mb-4">
                Look for action verbs and outcomes throughout the description. Phrases like "increase revenue," 
                "reduce costs," "improve efficiency," or "grow the team" indicate specific metrics you'll be 
                measured against.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Extract Success Metrics:</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quantitative goals: revenue targets, team size, process improvements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Qualitative expectations: leadership style, communication approach</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Implicit priorities: what's mentioned first and repeated often</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Stakeholder relationships: who you'll work with and report to</span>
                </li>
              </ul>
            </section>

            <section id="application" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Using Your Analysis in Applications
              </h2>
              <p className="text-gray-700 mb-4">
                Once you've analyzed the job description, use your insights strategically. Your resume and 
                cover letter should directly address the role's priorities using similar language.
              </p>
              <p className="text-gray-700 mb-4">
                Mirror the job description's terminology without copying it verbatim. If they mention "driving 
                cross-functional initiatives," describe your experience "leading cross-functional projects." 
                This creates subconscious alignment in the reviewer's mind.
              </p>
              <p className="text-gray-700 mb-4">
                Prioritize your resume content based on the job's emphasis. If the description spends more 
                words on technical skills than leadership, lead with your technical achievements. This 
                signals that you understand what matters most for the role.
              </p>
            </section>

            <section id="interview" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Leveraging Analysis in Interviews
              </h2>
              <p className="text-gray-700 mb-4">
                Your job description analysis becomes even more valuable during interviews. Use it to anticipate 
                questions, prepare relevant examples, and ask insightful questions of your own.
              </p>
              <p className="text-gray-700 mb-4">
                For each key responsibility in the job description, prepare a STAR story demonstrating your 
                relevant experience. For each required skill, have a specific example ready. This preparation 
                ensures you're never caught without a relevant answer.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 my-6">
                <p className="text-blue-800 font-medium">
                  Pro Tip: Ask questions that reference specific phrases from the job description. 
                  "The posting mentioned 'driving transformation' — what does success look like in 
                  the first 90 days?" This demonstrates thorough preparation and genuine interest.
                </p>
              </div>
            </section>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Automate Your Job Description Analysis
            </h3>
            <p className="text-blue-100 mb-6">
              Hireun automatically analyzes job descriptions and aligns them with your CV, generating 
              personalized interview questions and answers based on the specific requirements of each role.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              Try Hireun Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>

          {/* Related Guides */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Related Guides</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/guides/interview-preparation" className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-1">Complete Interview Preparation</h4>
                <p className="text-sm text-gray-600">Master the full interview preparation process.</p>
              </Link>
              <Link to="/guides/senior-interview-questions" className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-1">Senior Interview Questions</h4>
                <p className="text-sm text-gray-600">Strategies for executive and leadership interviews.</p>
              </Link>
            </div>
          </div>
        </article>

        <PublicFooter />
      </div>
    </>
  );
};

export default JobDescriptionAnalysisGuidePage;
